import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/db/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/shared/PasswordInput";
import { getAppUrl } from "@/lib/appUrl";
import { useTenant, useHostTenant, useCookieDomain, tenantReachable } from "@/hooks/useTenant";
import TenantWordmark from "@/components/shared/TenantWordmark";
import { Logo } from "@/components/shared/Logo";
import { EVERON_LOGO } from "@/lib/constants";

type Mode = "signin" | "forgot";

const Login = () => {
  const { id: tenantId, name: tenantName, logo: tenantLogo, contact_email: contactEmail } = useTenant();
  // The shared "Welcome" host brands nobody: it is not a workspace anyone signs
  // in TO, it is the door in front of all of them. Wearing the default tenant's
  // identity here would name a workspace the person is not about to enter.
  const { isDefault: atSharedHost } = useHostTenant();
  const cookieDomain = useCookieDomain();
  const navigate = useNavigate();
  const location = useLocation();
  // AppLayout / the re-login prompt stash the page the user was aiming at; send
  // them back there once signed in, falling back to the dashboard.
  const from = (location.state as { from?: string } | null)?.from ?? "/";
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // The browser is fetching another host's document. Nothing here can hurry
  // that along, so the page says what is happening instead of sitting on a
  // filled-in form that looks like it did not submit.
  const [leaving, setLeaving] = useState(false);

  // True from the moment this page submits a sign-in until it has decided where
  // that sign-in goes. A ref, not state: it is read inside the auth listener,
  // which fires DURING the submit, and a state update would not have landed yet.
  const decidingDestination = useRef(false);

  useEffect(() => {
    // Check if user is already logged in
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        navigate(from, { replace: true });
      }
    });

    // No PASSWORD_RECOVERY guard: unlike Supabase, our auth service never puts
    // the browser into a recovery session. /reset-password takes the token
    // straight from the emailed link, so there is no event to ignore here.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      // Our own submit owns where it lands, so this stands aside for it. The
      // session is stored SYNCHRONOUSLY inside signInWithPassword, so this
      // listener runs while handleLogin is still suspended at its await: left
      // to itself it would route to `from` — the landing page — and the
      // workspace redirect would then have to navigate away from a page the
      // user was never meant to see. That flash is the bug, not the timing.
      if (decidingDestination.current) return;
      if (session) {
        navigate(from, { replace: true });
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, from]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    decidingDestination.current = true;

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    // At the shared host the server also says where this session belongs: the
    // one workspace this person has, or the one they starred. Going there is a
    // full navigation to another host, which the parent-domain cookie carries
    // the session across — so it is done here rather than left to the router.
    // A host the cookie cannot reach is no shortcut at all (it would ask them
    // to sign in again on arrival), so that case falls through to the landing
    // page, where their workspaces are listed.
    if (!error && data.redirectHost && tenantReachable(data.redirectHost, cookieDomain)) {
      // `loading` stays set and the flag stays raised: the document is on its
      // way out, and the last thing this page should do is re-enable a form
      // the user is finished with.
      setLeaving(true);
      const port = window.location.port ? `:${window.location.port}` : "";
      window.location.assign(`${window.location.protocol}//${data.redirectHost}${port}/`);
      return;
    }

    decidingDestination.current = false;
    setLoading(false);

    if (error) {
      // Show the server's message: it distinguishes bad credentials from the
      // 15-minute rate-limit lockout ("Too many attempts...") and network errors.
      setError(error.message);
      return;
    }

    // Signed in with nowhere particular to be sent — the workspace picker, or
    // an ordinary tenant-host sign-in. The listener stood aside, so this page
    // does the routing it would have done.
    navigate(from, { replace: true });
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${getAppUrl()}/reset-password`,
    });

    setLoading(false);

    if (error) {
      setError(error.message);
      return;
    }

    toast.success("Check your email for a reset link");
    setMode("signin");
    setPassword("");
  };

  const switchMode = (next: Mode) => {
    setMode(next);
    setError("");
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md">
        {/* The sign-in form lives in a single card so it reads as one object on
            the page. The tenant co-brand sits in a muted cap across the top —
            present as "whose workspace this is" without competing with the
            Backlog product identity, which is the hero inside the card. */}
        <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-lg">
          {!atSharedHost && (
            <div className="flex justify-center border-b border-border bg-muted/60 px-6 py-3.5">
              <TenantWordmark logo={tenantLogo} name={tenantName} tenantId={tenantId} hideLabel />
            </div>
          )}

          <div className="space-y-8 px-8 py-9">
            <div className="text-center">
              <h1 className="flex items-center justify-center gap-2.5">
                <Logo className="h-9 w-9" />
                <span className="text-3xl font-bold tracking-tight">Backlog</span>
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {leaving
                  ? "Taking you to your workspace…"
                  : mode === "forgot"
                    ? "Reset your password — we'll email you a link to set a new one."
                    : atSharedHost
                      ? "Sign in and we'll take you to your workspace"
                      : "Sign in to access the backlog"}
              </p>
            </div>

            {/* Mid-hop: the credentials are accepted and another host's
                document is on its way. Keeping the filled-in form on screen
                would read as "nothing happened", so it stands down. */}
            {leaving ? null : mode === "signin" ? (
              <form onSubmit={handleLogin} className="mt-8 space-y-6">
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="email">Email address</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setError("");
                      }}
                      required
                      className="mt-1"
                      placeholder="you@everon.tech"
                    />
                  </div>

                  <div>
                    <div className="flex items-baseline justify-between">
                      <Label htmlFor="password">Password</Label>
                      <button
                        type="button"
                        onClick={() => switchMode("forgot")}
                        className="text-xs font-medium text-brand hover:underline"
                      >
                        Forgot password?
                      </button>
                    </div>
                    <PasswordInput
                      id="password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setError("");
                      }}
                      required
                      placeholder="••••••••"
                      className="mt-1"
                    />
                  </div>

                  {error && <div className="text-sm text-destructive font-medium">{error}</div>}
                </div>

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? "Signing in..." : "Sign in"}
                </Button>

                {/* No self-service registration — an admin has to invite you.
                    Points at the tenant's own contact email rather than the
                    dead /create-account page (which 404s meaningfully without
                    a token), and disappears entirely if no one has set one. */}
                {contactEmail && (
                  <div className="space-y-2">
                    <p className="text-center text-sm text-muted-foreground">
                      Don't have an account?{" "}
                      <a href={`mailto:${contactEmail}`} className="font-medium text-brand hover:underline">
                        Request an invite
                      </a>
                    </p>
                  </div>
                )}
              </form>
            ) : (
              <form onSubmit={handleForgotPassword} className="mt-8 space-y-6">
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="reset-email">Email address</Label>
                    <Input
                      id="reset-email"
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setError("");
                      }}
                      required
                      className="mt-1"
                      placeholder="you@everon.tech"
                    />
                  </div>

                  {error && <div className="text-sm text-destructive font-medium">{error}</div>}
                </div>

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? "Sending..." : "Send reset link"}
                </Button>

                <p className="text-center text-sm text-muted-foreground">
                  <button
                    type="button"
                    onClick={() => switchMode("signin")}
                    className="font-medium text-brand hover:underline"
                  >
                    ← Back to sign in
                  </button>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>

      <footer className="absolute inset-x-0 bottom-6 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
        <span>Powered by</span>
        <img src={EVERON_LOGO} alt="" className="h-4 w-4" />
        <span className="font-medium">Everon</span>
      </footer>
    </div>
  );
};

export default Login;
